import { useState } from "react";
import { Trophy, ChevronDown, ChevronUp, Clock } from "lucide-react";

export default function PointsTable({ standings = [] }) {
  const [expanded, setExpanded] = useState([]);

  // Remove duplicate teams
  const uniqueTeams = standings.filter(
    (team, index, self) =>
      index ===
      self.findIndex(
        (t) =>
          t.team_id === team.team_id &&
          t.stage_name === team.stage_name
      )
  );

  const toggleExpand = (teamId) => {
    setExpanded((prev) =>
      prev.includes(teamId)
        ? prev.filter((r) => r !== teamId)
        : [...prev, teamId]
    );
  };

  return (
    <div
      id="tournament-points-table"
      className="mt-6 mb-8 scroll-mt-20"
    >
      <div className="rounded-2xl overflow-hidden border border-gray-200/50 dark:border-gray-700/50 bg-white/95 dark:bg-gray-900/95 backdrop-blur shadow-xl">

        {/* Header */}
        <div className="px-4 py-4 bg-gradient-to-r from-blue-500/10 via-transparent to-purple-500/10 border-b border-gray-200 dark:border-gray-700">

          <div className="flex items-center justify-between flex-wrap gap-3">

            <div className="flex items-center gap-3">

              <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                <Trophy className="w-4 h-4 text-blue-500" />
              </div>

              <h3 className="font-bold text-lg text-gray-900 dark:text-white truncate">
                {uniqueTeams?.[0]?.league_name || "League"} - Points Table
              </h3>

            </div>

            <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800">
              {uniqueTeams.length} Teams
            </span>

          </div>
        </div>

        {/* No Data */}
        {uniqueTeams.length === 0 ? (
          <div className="py-16 text-center">

            <div className="text-5xl mb-3">
              ⚽
            </div>

            <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
              No Points Table Found
            </h3>

            <p className="text-sm text-gray-500 mt-1">
              No standings available right now.
            </p>

          </div>
        ) : (
          <>
            {/* Table Head */}
            <div className="sticky top-0 z-10 px-4 py-3 bg-gray-100/70 dark:bg-gray-800/70 border-b">

              <div className="flex justify-between">

                <span className="text-xs font-semibold uppercase text-gray-500">
                  Teams
                </span>

                <div className="flex gap-3 text-xs text-gray-500">

                  <span className="w-6 text-center">P</span>
                  <span className="w-6 text-center">W</span>
                  <span className="w-6 text-center">L</span>
                  <span className="w-8 text-center">PTS</span>
                  <span className="w-14 text-center">GF</span>

                </div>

              </div>
            </div>

            {/* Rows */}
            {uniqueTeams.map((team) => (
              <div
                key={`${team.team_id}-${team.stage_name}`}
                className="border-b border-gray-200 dark:border-gray-700"
              >
                <div
                  onClick={() => toggleExpand(team.team_id)}
                  className="
                    px-4 py-3
                    cursor-pointer
                    flex
                    items-center
                    justify-between
                    hover:bg-gray-50
                    dark:hover:bg-gray-800/50
                    transition-colors
                  "
                >
                  {/* Left */}
                  <div className="flex items-center gap-3 min-w-0">

                    <span className="w-5 text-sm text-gray-500">
                      {team.overall_league_position}
                    </span>

                    <img
                      src={team.team_badge}
                      alt={team.team_name}
                      className="w-8 h-8 object-contain"
                    />

                    <div className="min-w-0">

                      <div className="font-semibold text-blue-500 truncate">
                        {team.team_name}
                      </div>

                      {team.overall_promotion && (
                        <div className="text-[10px] text-green-500 truncate">
                          {team.overall_promotion}
                        </div>
                      )}

                    </div>

                  </div>

                  {/* Right */}
                  <div className="flex items-center gap-3 text-sm">

                    <span className="w-6 text-center">
                      {team.overall_league_payed}
                    </span>

                    <span className="w-6 text-center text-green-500 font-medium">
                      {team.overall_league_W}
                    </span>

                    <span className="w-6 text-center text-red-500 font-medium">
                      {team.overall_league_L}
                    </span>

                    <span className="w-8 text-center font-bold">
                      {team.overall_league_PTS}
                    </span>

                    <span className="w-14 text-center font-medium">
                      {team.overall_league_GF}
                    </span>

                    {expanded.includes(team.team_id) ? (
                      <ChevronUp className="w-4 h-4 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-500" />
                    )}

                  </div>
                </div>

                {/* Expand Details */}
                {expanded.includes(team.team_id) && (
                  <div className="px-4 py-4 bg-gray-50 dark:bg-gray-800/30">

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">

                      <Stat
                        value={team.overall_league_payed}
                        label="Played"
                      />

                      <Stat
                        value={team.overall_league_W}
                        label="Won"
                      />

                      <Stat
                        value={team.overall_league_D}
                        label="Draw"
                      />

                      <Stat
                        value={team.overall_league_L}
                        label="Lost"
                      />

                      <Stat
                        value={team.overall_league_GF}
                        label="Goals For"
                      />

                      <Stat
                        value={team.overall_league_GA}
                        label="Goals Against"
                      />

                      <Stat
                        value={team.overall_league_PTS}
                        label="Points"
                      />

                      <Stat
                        value={team.stage_name}
                        label="Stage"
                      />

                    </div>

                    <div className="mt-4 pt-3 border-t text-center text-sm text-gray-500">
                      {team.country_name} • {team.league_name}
                    </div>

                  </div>
                )}
              </div>
            ))}

            {/* Footer */}
            {/* <div className="flex items-center justify-center gap-2 py-3 text-xs text-gray-500">

              <Clock className="w-3 h-3" />
              Auto-updated standings

            </div> */}
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div className="text-center">
      <div className="font-bold text-lg">
        {value || "-"}
      </div>

      <div className="text-xs text-gray-500">
        {label}
      </div>
    </div>
  );
}